import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { Button, FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import RotatingLoader from '../../common/ui/RotatingLoader';
import {
  AccountData,
  OrderPageData,
  OrderPageTypes,
  QualifiedOrderPageData,
  UserPreferencesOut,
  UserPreferencesService,
  UserPreferencesUpdateDTO,
} from '../../api';
import { OrderPagesMultiSelect } from '../OrderPagesMultiSelect';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPages } from '../../common/AccountUtils';
import { useLanguage } from '../../common/GeneralUtils';
import { WorkshopCircle } from '../../common/ui/WorkshopCircle';

type QualifiedOrderPageFormSettings = QualifiedOrderPageData & {
  has_teaching_limit: boolean;
};
type QualificationsFormValues = Omit<
  UserPreferencesUpdateDTO,
  'qualified_order_pages_settings' | 'qualified_order_pages'
> & {
  qualified_order_pages: string[];
  qualified_order_pages_settings: QualifiedOrderPageFormSettings[];
};
type PermanentUserAvailabilityProps = {
  userPreferences?: UserPreferencesOut;
  isLoadingUserPreferences: boolean;
  refetchUserPrefs: () => Promise<any>;
  selectedUserId?: string;
};
export const Qualifications = ({
  userPreferences,
  isLoadingUserPreferences,
  refetchUserPrefs,
  selectedUserId,
}: PermanentUserAvailabilityProps) => {
  const language = useLanguage();
  const { t } = useTranslation();
  const [isRefetching, setIsRefetching] = useState(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const orderPages = getOrderPages(account);
  useEffect(() => {
    if (selectedUserId === userPreferences?.propel_id) {
      return;
    }
    const refetchDataWithNewUser = async () => {
      setIsRefetching(true);
      await refetchUserPrefs();
      setIsRefetching(false);
    };

    void refetchDataWithNewUser();
  }, [selectedUserId]);

  if (isLoadingUserPreferences || isRefetching || isUpdatingSettings) {
    return <RotatingLoader />;
  }

  const initialValues: QualificationsFormValues = {
    should_send_shift_notifications: userPreferences?.should_send_shift_notifications || false,
    should_send_workshop_assignment_notifications:
      userPreferences?.should_send_workshop_assignment_notifications || false,
    qualified_order_pages: userPreferences?.qualified_order_pages_settings?.map((x) => x.order_page_id) || [],
    qualified_order_pages_settings: (userPreferences?.qualified_order_pages_settings || []).map(
      (qualiOrderPageData) => ({
        order_page_id: qualiOrderPageData.order_page_id,
        max_teaching_capacity: qualiOrderPageData?.max_teaching_capacity || 0,
        has_teaching_limit: Boolean(qualiOrderPageData?.max_teaching_capacity),
      }),
    ),
    user_id: selectedUserId,
  };

  const onSubmit = async (values: QualificationsFormValues) => {
    setIsUpdatingSettings(true);
    const dto: UserPreferencesUpdateDTO = {
      ...values,
      qualified_order_pages_settings: values.qualified_order_pages.map((orderPageId) => {
        const setting = values.qualified_order_pages_settings.find((s) => s.order_page_id === orderPageId);
        return {
          order_page_id: orderPageId,
          has_teaching_limit: setting?.has_teaching_limit || false,
          max_teaching_capacity: setting?.max_teaching_capacity || undefined,
        };
      }),
    };
    await UserPreferencesService.editUserPreferences(dto);
    await refetchUserPrefs();
    setIsUpdatingSettings(false);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
      {({ values, handleSubmit, handleChange, setFieldValue, errors, touched }) => (
        <Form onSubmit={handleSubmit}>
          <Grid
            container
            flexDirection="column"
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              gap: 2,
            }}>
            <Typography variant="h5" fontWeight={700}>
              {t('userSettings.qualification')}
            </Typography>
            <OrderPagesMultiSelect
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              orderPagesFieldName="qualified_order_pages"
              account={account}
            />
            <Typography variant="h5" fontWeight={700}>
              {t('userSettings.extraLimitations')}
            </Typography>
            {values.qualified_order_pages
              .filter(
                (orderPageId) =>
                  orderPages?.find((x: OrderPageData) => x.id === orderPageId)?.order_page_type ===
                  OrderPageTypes.PRIVATE,
              )
              .map((orderPageId) => (
                <Grid key={orderPageId} container spacing={2} alignItems="center">
                  <Grid container item xs={4} gap={1} alignItems={'center'}>
                    <Grid>
                      <WorkshopCircle account={account} orderPageId={orderPageId} language={language} />
                    </Grid>
                    <Typography>{orderPages.find((p: any) => p.id === orderPageId)?.label[language]}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            values.qualified_order_pages_settings.find((s) => s.order_page_id === orderPageId)
                              ?.has_teaching_limit || false
                          }
                          onChange={async (e) => {
                            const newSettings = [...values.qualified_order_pages_settings];
                            const index = newSettings.findIndex((s) => s.order_page_id === orderPageId);
                            if (index >= 0) {
                              newSettings[index] = { ...newSettings[index], has_teaching_limit: e.target.checked };
                            } else {
                              newSettings.push({
                                order_page_id: orderPageId,
                                has_teaching_limit: e.target.checked,
                                max_teaching_capacity: 0,
                              });
                            }
                            await setFieldValue('qualified_order_pages_settings', newSettings);
                          }}
                        />
                      }
                      label={t('userSettings.enableTeachingLimit')}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      type="number"
                      label={t('userSettings.maxTeachingCapacity')}
                      value={
                        values.qualified_order_pages_settings.find((s) => s.order_page_id === orderPageId)
                          ?.max_teaching_capacity || 0
                      }
                      onChange={async (e) => {
                        const newSettings = [...values.qualified_order_pages_settings];
                        const index = newSettings.findIndex((s) => s.order_page_id === orderPageId);
                        if (index >= 0) {
                          newSettings[index] = {
                            ...newSettings[index],
                            max_teaching_capacity: parseInt(e.target.value, 10),
                          };
                        } else {
                          newSettings.push({
                            order_page_id: orderPageId,
                            has_teaching_limit: true,
                            max_teaching_capacity: parseInt(e.target.value, 10),
                          });
                        }
                        await setFieldValue('qualified_order_pages_settings', newSettings);
                      }}
                      disabled={
                        !values.qualified_order_pages_settings.find((s) => s.order_page_id === orderPageId)
                          ?.has_teaching_limit
                      }
                    />
                  </Grid>
                </Grid>
              ))}

            <Grid container justifyContent="flex-end" mt={2} mb={10}>
              <Button type="submit" variant="contained" disabled={isUpdatingSettings}>
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
